import { useComposedRefs } from "@radix-ui/react-compose-refs";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { cx } from "class-variance-authority";
import { AnimatePresence } from "motion/react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useDragToScroll } from "../hooks/useDragToScroll";
import {
  useScrollBoundary,
  type ScrollOrientation,
} from "../hooks/useScrollBoundary";
import { Shadow } from "../Shadow/Shadow";
import {
  useScrollIntoViewOnce,
  type ScrollIntoViewAlignment,
} from "./useScrollIntoViewOnce";

import * as styles from "./ScrollArea.module.scss";

type ScrollAreaContextValue = ReturnType<typeof useScrollBoundary> & {
  orientation: ScrollOrientation;
};

const ScrollAreaContext = createContext<ScrollAreaContextValue | null>(null);

const useScrollAreaContext = () => {
  const context = useContext(ScrollAreaContext);
  if (!context) {
    throw new Error(
      "ScrollArea compound parts must be rendered inside <ScrollArea.Root>",
    );
  }
  return context;
};

type RootProps = {
  children: ReactNode;
  className?: string;
  /** passed straight through to Radix — controls when its own scrollbar
   * affordance appears ("hover" default, "scroll", "auto", or "always").
   * Irrelevant unless ScrollArea.Scrollbar is composed.
   */
  type?: React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.Root
  >["type"];
  orientation: ScrollOrientation;
};

const Root = ({
  children,
  className,
  type,
  orientation,
}: RootProps) => {
  const scrollBoundary = useScrollBoundary(orientation);
  const { ref, isStart, isEnd, isScrollable } = scrollBoundary;
  const contextValue = useMemo(
    () => ({ ref, isStart, isEnd, isScrollable, orientation }),
    [ref, isStart, isEnd, isScrollable, orientation],
  );
  return (
    <ScrollAreaContext.Provider value={contextValue}>
      <ScrollAreaPrimitive.Root
        type={type}
        className={cx(styles.container, className)}
      >
        {children}
        {/* Radix only sets overflow-x/y to "scroll" (via inline style) on an
            axis that has a ScrollAreaScrollbar mounted for it — otherwise
            it's "hidden", which blocks real touch/wheel/drag input. A CSS class
            can't fix this from outside: Radix's inline style always wins over a
            class rule. Mounting an actual (invisible) ScrollAreaScrollbar instead
            gets Radix's own state to agree honestly — no fighting, no mismatch. */}
        <ScrollAreaPrimitive.ScrollAreaScrollbar
          orientation={orientation}
          className={cx(styles.scrollBar, styles.invisible)}
        >
          <ScrollAreaPrimitive.ScrollAreaThumb className={styles.thumb} />
        </ScrollAreaPrimitive.ScrollAreaScrollbar>
      </ScrollAreaPrimitive.Root>
    </ScrollAreaContext.Provider>
  );
};
Root.displayName = "ScrollArea.Root";

type ViewportProps = {
  children: ReactNode;
  className?: string;
  /** Native free-scroll by default. Opt in to align a clicked interactive
   * child to the start/center/end of the viewport's scroll axis — the other
   * axis always stays "nearest" (never moves).
   */
  scrollIntoView?: ScrollIntoViewAlignment;
  dragToScroll?: boolean;
  /** CSS scroll-snap, touch-only devices
   */
  snap?: boolean;
  /** Radix asChild passthrough — merges Radix's viewport props/ref onto the
   * single child instead of wrapping it in an extra div.
   */
  asChild?: boolean;
};

const Viewport = ({
  children,
  className,
  scrollIntoView,
  dragToScroll,
  snap,
  asChild,
  ref: forwardedRef,
}: ViewportProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { ref, orientation, isScrollable } = useScrollAreaContext();
  const composedRef = useComposedRefs(ref, forwardedRef);

  const isDragging = useDragToScroll(ref, Boolean(dragToScroll), orientation);
  const onClickScrollIntoView = useScrollIntoViewOnce(
    ref,
    orientation,
    scrollIntoView,
  );

  const isHorizontal = orientation === "horizontal";

  return (
    <ScrollAreaPrimitive.Viewport
      ref={composedRef}
      asChild={asChild}
      className={cx(
        styles.viewport,
        {
          [styles.snap]: snap && isHorizontal,
          [styles.snapVertical]: snap && !isHorizontal,
          [styles.grab]: dragToScroll,
          [styles.dragging]: isDragging,
          [styles.overscrollX]: isHorizontal && isScrollable,
          [styles.viewportPaddingX]: isHorizontal,
          [styles.viewportPaddingY]: !isHorizontal,
        },
        className,
      )}
      onClick={scrollIntoView ? onClickScrollIntoView : undefined}
    >
      {children}
    </ScrollAreaPrimitive.Viewport>
  );
};
Viewport.displayName = "ScrollArea.Viewport";

type ShadowPartProps = {
  className?: string;
};

const ShadowStart = ({ className }: ShadowPartProps) => {
  const { isStart, isScrollable, orientation } = useScrollAreaContext();
  const position = orientation === "horizontal" ? "left" : "top";
  return (
    <AnimatePresence>
      {isScrollable && !isStart && (
        <Shadow className={className} position={position} />
      )}
    </AnimatePresence>
  );
};
ShadowStart.displayName = "ScrollArea.ShadowStart";

const ShadowEnd = ({ className }: ShadowPartProps) => {
  const { isEnd, isScrollable, orientation } = useScrollAreaContext();
  const position = orientation === "horizontal" ? "right" : "bottom";
  return (
    <AnimatePresence>
      {isScrollable && !isEnd && (
        <Shadow className={className} position={position} />
      )}
    </AnimatePresence>
  );
};
ShadowEnd.displayName = "ScrollArea.ShadowEnd";

const Shadows = ({ className }: ShadowPartProps) => (
  <>
    <ShadowStart className={className} />
    <ShadowEnd className={className} />
  </>
);
Shadows.displayName = "ScrollArea.Shadows";

type ScrollbarProps = {
  className?: string;
  lightScrollbar?: boolean;
  revealOnScroll?: boolean;
  /** key for safari to target and recompute the thumb size when content grows while the
   * scrollbar stays mounted.
   */
  remeasureKey?: React.Key;
  // Thumb has no independent mount/mechanics of its own — it's always
  // rendered together with the bar it lives in, never composed separately —
  // so this is a plain passthrough rather than a promoted ScrollArea.Thumb
  // part. See Search's themed thumb color for the one real consumer.
  thumbClassName?: string;
};

const Scrollbar = ({
  className,
  lightScrollbar,
  revealOnScroll = false,
  remeasureKey,
  thumbClassName,
}: ScrollbarProps) => {
  const { ref, orientation } = useScrollAreaContext();
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (!revealOnScroll) return;
    const el = ref.current;
    if (!el) return;

    let idle = 0;
    const onScroll = () => {
      setIsScrolling(true);
      window.clearTimeout(idle);
      idle = window.setTimeout(() => setIsScrolling(false), 700);
    };

    el.addEventListener("scroll", onScroll);
    return () => {
      window.clearTimeout(idle);
      el.removeEventListener("scroll", onScroll);
    };
  }, [ref, revealOnScroll]);

  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      key={remeasureKey}
      orientation={orientation}
      data-testid={`scroll-area-scrollbar-${orientation}`}
      className={cx(
        styles.scrollBar,
        {
          [styles.vertical]: orientation === "vertical",
          [styles.horizontal]: orientation === "horizontal",
          [styles.revealable]: revealOnScroll,
          [styles.revealed]: !revealOnScroll || isScrolling,
        },
        className,
      )}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        className={cx(
          styles.thumb,
          { [styles.light]: lightScrollbar },
          thumbClassName,
        )}
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
};
Scrollbar.displayName = "ScrollArea.Scrollbar";

type ScrollAreaProps = {
  children: ReactNode;
  /** applied to the outer container (Root), not the scrollable viewport —
   * needing a *Viewport*-level className (or any other Viewport prop) is
   * exactly the signal to compose the parts manually instead. */
  className?: string;
  orientation: ScrollOrientation;
  ref?: React.Ref<HTMLDivElement>;
};

/**
 * ScrollAreaBase to render plain scroll area with scroll bar.
 * Compose the parts (Viewport/Shadows/Scrollbar) to change the layout
 */
const ScrollAreaBase = ({
  children,
  className,
  orientation,
  ref,
}: ScrollAreaProps) => (
  <Root className={className} orientation={orientation}>
    <Viewport ref={ref}>{children}</Viewport>
    <Scrollbar />
  </Root>
);
ScrollAreaBase.displayName = "ScrollArea";

const ScrollArea = Object.assign(ScrollAreaBase, {
  Root,
  Viewport,
  Shadows,
  ShadowStart,
  ShadowEnd,
  Scrollbar,
});

export { ScrollArea };
